import { Component, OnInit, Inject, ChangeDetectorRef, OnDestroy } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatCheckboxChange } from '@angular/material/checkbox';
import { MatRadioChange } from '@angular/material/radio';
import { MeteringType, MeteringFilterType } from '@enums';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import {
  MeteringPointGroup,
  MeteringPoint,
  MeteringPointAddress,
  MeteringFilter,
  MeteringSubFilter,
  MeteringPointStub
} from '@model';
import * as fromMeteringStore from '@appStore/metering-points';
import * as fromAccountStore from '@appStore/account';
import * as fromCustomerStore from '@appStore/customers';
import { Subject } from 'rxjs';
import { distinctUntilChanged, map, filter, debounceTime } from 'rxjs/operators';
import { containsCaseInsensitive } from '@utils/sx-utilities';
import { SubSink } from 'subsink';


@Component({
    selector: 'app-metering-filter-config-form',
    templateUrl: './metering-filter-config-form.component.html',
    standalone: false
})
export class MeteringFilterConfigFormComponent implements OnInit, OnDestroy {
  searchFilter: string;
  isSearchAddresses: boolean;
  currentFilter: MeteringFilter;

  public keyUp = new Subject<any>();

  meteringType: MeteringType;
  meteringPoints: MeteringPoint[];
  meteringPointsView: MeteringPoint[];
  meteringPointGroups: MeteringPointGroup[];
  meteringPointGroupsView: MeteringPointGroup[];

  selectedMeteringPoints: MeteringPoint[] = [];
  selectedMeteringPointGroup: MeteringPointGroup;
  selectedVirtual = false;
  virtualGroupActivated = false;
  virtualGroupMeteringPointIds: number[] = [];

  meteringTypeEnum = MeteringType;
  meteringPointSelectedWatch: Subject<{checked: boolean, meteringPoint: MeteringPoint}>;

  private subs = new SubSink();
  filterArea: string;

  constructor(
    private accountStore: Store<fromAccountStore.State>,
    private meteringStore: Store<fromMeteringStore.State>,
    private customerStore: Store<fromCustomerStore.State>,
    public dialogRef: MatDialogRef<MeteringFilterConfigFormComponent>,
    private changeDetectorRef: ChangeDetectorRef,
    private translateService: TranslateService,
    @Inject(MAT_DIALOG_DATA)
    public data: { meteringType: MeteringType; filterSettings: MeteringFilter; filterArea: string }
  ) {
    this.keyUp
      .pipe(
        map(event => event.target.value),
        debounceTime(300),
        distinctUntilChanged()
      )
      .subscribe(filt => {
        this.updateView(filt);
      });
    this.filterArea = data.filterArea;
    this.selectedVirtual = data.filterSettings.filterType === MeteringFilterType.VirtualFilter;
  }

  get isAllMeteringPointsChecked() : boolean{
    return this.selectedMeteringPoints.length === 0 && 
    (!(!!this.selectedMeteringPointGroup) || this.selectedMeteringPointGroup.id === -1) && 
    !this.selectedVirtual;
  }

  get isShowSelectedMeteringPoints() : boolean{
    if (!(!!this.selectedMeteringPointGroup))
      return true;

    return this.selectedVirtual || this.selectedMeteringPoints.length > 0;
  }

  get isLogical() : boolean{
    return this.meteringType === MeteringType.HotLogical || this.meteringType === MeteringType.ColdLogical;
  }

  ngOnInit() {
    this.searchFilter = '';
    this.isSearchAddresses = false;
    this.meteringType = this.data.meteringType;

    this.currentFilter = this.data.filterSettings;

    let virtualGroupState$ = this.meteringStore.select(fromMeteringStore.getVirtualGroup);

    this.subs.sink = virtualGroupState$.pipe(filter(x => !!x)).subscribe(x => {
      if (this.meteringType === MeteringType.Hot) {
        this.virtualGroupActivated = x.hotActivated;
        this.virtualGroupMeteringPointIds = this.virtualGroupActivated ? x.hot : [];
      } else if (this.meteringType === MeteringType.Cold) {
        this.virtualGroupActivated = x.coldActivated;
        this.virtualGroupMeteringPointIds = this.virtualGroupActivated ? x.cold : [];
      } else {
        this.virtualGroupActivated = false;
        this.virtualGroupMeteringPointIds = [];
      }
      if (this.virtualGroupActivated){
        this.selectedMeteringPointGroup = { id: -2 }; // valda anläggningar
        this.selectedVirtual = true;
      } else{
        this.selectedMeteringPointGroup = { id: -1 }; // alla anläggningar
      }
    });

    if (this.isLogical) {
      this.initLogicalMpSubscription();
    } else {
      this.initNormalMpSubscription();
    }
    this.initNormalMpGroupSubscription();
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  initLogicalMpSubscription = () => {
    let meteringType = this.meteringType === MeteringType.HotLogical ? MeteringType.Hot : MeteringType.Cold;

    this.subs.sink = this.meteringStore.select(fromMeteringStore.getMeteringPointsWithAdresses).subscribe(mps => {
      this.meteringPoints = mps
        .filter(mp => mp.meteringType === meteringType && mp.type === 'LOG')
        .map(mp => ({
          ...mp,
          selected:
            this.currentFilter &&
            this.currentFilter.filterType === MeteringFilterType.MeteringPoint 
            && this.currentFilter.filterId === mp.id

        }));
      this.meteringPointsView = this.meteringPoints;
      this.selectedMeteringPoints = this.meteringPoints.filter(mp => mp.selected);
    });
  };

  initNormalMpSubscription = () => {
    this.subs.sink = this.meteringStore.select(fromMeteringStore.getMeteringPointsWithAdresses).subscribe(mps => {

      // Load meteringPoint if customer is not white label
      this.subs.sink = this.customerStore.select(fromCustomerStore.getCustomers).subscribe(customers => {
      
        // Filter out mpIds where customer is white label
        if (customers) {
          mps = mps.filter(mp => {
            let customer = customers.find(c => c.id === mp.customerId);
            return customer ? !customer.isWhiteLabel : false;
          });

          this.meteringPoints = mps
            .filter(mp => mp.meteringType === this.meteringType)
            .map(mp => ({
              ...mp,
              selected:
                this.currentFilter &&
                (this.currentFilter.filterType === MeteringFilterType.MeteringPoint && this.currentFilter.filterId === mp.id) 
                || (this.currentFilter.filterType === MeteringFilterType.VirtualFilter && this.virtualGroupMeteringPointIds.some(virtualMpId => virtualMpId === mp.id))
          }));

          this.meteringPointsView = this.meteringPoints;
          this.selectedMeteringPoints = this.meteringPoints.filter(mp => mp.selected);
          //console.log(`initNormalMpSubscription\tselectedMeteringPoints`, this.selectedMeteringPoints);

        }
      });  
    });
  }

  initNormalMpGroupSubscription = () => {
    this.subs.sink = this.meteringStore.select(fromMeteringStore.getMeteringPointGroups).subscribe(mpgs => {
      this.meteringPointGroups = mpgs
        .filter(mpg => mpg.meteringType === this.meteringType && mpg.meteringPoints.length > 0)
        .map(mpg => ({
          ...mpg,
          selected:
            this.currentFilter &&
            this.currentFilter.filterType === MeteringFilterType.MeteringPointGroup &&
            this.currentFilter.filterId === mpg.id
        }));
      
      this.meteringPointGroupsView = this.meteringPointGroups;
    });

  };

  updateView(filterString: string) {
    if (this.isSearchAddresses) {
      if (filterString === '') {
        this.meteringPointsView = [...this.meteringPoints];
        this.meteringPointGroupsView = [];
      } else {
        this.meteringPointsView = [
          ...this.meteringPoints.filter(
            mp =>
              this.foundInAdresses(mp.addresses, filterString) ||
              containsCaseInsensitive(mp.id.toString(), filterString)
          )
        ];
        this.meteringPointGroupsView = [];
      }
    } else {
      if (filterString === '') {
        this.meteringPointsView = [...this.meteringPoints];
        this.meteringPointGroupsView = [...this.meteringPointGroups];
      } else {
        this.meteringPointsView = [
          ...this.meteringPoints.filter(
            mp =>
              containsCaseInsensitive(mp.name, filterString) || containsCaseInsensitive(mp.id.toString(), filterString)
          )
        ];
        this.meteringPointGroupsView = [
          ...this.meteringPointGroups.filter(mp => containsCaseInsensitive(mp.name, filterString))
        ];
      }
    }
    this.changeDetectorRef.detectChanges();
  }

  onCancel() {
    this.dialogRef.close('cancel');
  }

  onSave() {
    // Check for fake group if filter should be cleared
    let isVirtualGroup = false;
    if (this.selectedMeteringPointGroup){
      if (this.selectedMeteringPointGroup.id === -2) {
        this.currentFilter = {
          ...this.currentFilter,
          filterId: -1,
          filterType: MeteringFilterType.VirtualFilter,
          meteringType: this.meteringType,
          containsMeteringPointIds: [...this.virtualGroupMeteringPointIds],
          subfilter: null
        };
      } else if (this.selectedMeteringPointGroup.id === -1) {
        this.currentFilter = {
          ...this.currentFilter,
          filterId: -1,
          filterType: MeteringFilterType.Unfiltered,
          meteringType: this.meteringType,
          subfilter: null
        };
      } else if (this.selectedMeteringPointGroup.id >= 0) {
        this.currentFilter = {
          ...this.currentFilter,
          filterId: this.selectedMeteringPointGroup.id,
          filterType: MeteringFilterType.MeteringPointGroup,
          meteringType: this.meteringType,
          subfilter: null
        };
      }
    } else if (this.selectedMeteringPoints.length === 1) {
      let subFilter: MeteringSubFilter = null;
      if (this.meteringType === MeteringType.HotLogical || this.meteringType === MeteringType.ColdLogical) {
        subFilter = <MeteringSubFilter>{
          meteringType: this.meteringType,
          includedMeteringPoints: this.selectedMeteringPoints[0].containedMeteringPoints.map(
            mp => <MeteringPointStub>{ id: mp.id, name: mp.name, selected: true }
          )
        };
      }
      
      this.currentFilter = {
        ...this.currentFilter,
        filterType: MeteringFilterType.MeteringPoint,
        filterId: this.selectedMeteringPoints[0].id,
        meteringType: this.meteringType,
        name: this.selectedMeteringPoints[0].name,
        containsMeteringPointIds: this.selectedMeteringPoints[0].containedMeteringPoints
          ? this.selectedMeteringPoints[0].containedMeteringPoints.map(x => x.id)
          : [],
        subfilter: subFilter
      };
    } else if (this.selectedMeteringPoints.length > 1){
      this.currentFilter = {
        ...this.currentFilter,
        id: -1,
        filterId: -1,
        filterType: MeteringFilterType.VirtualFilter,
        meteringType: this.meteringType,
        containsMeteringPointIds: this.selectedMeteringPoints.map(mp => mp.id),
        subfilter: null,
        subtitle: '' + this.selectedMeteringPoints.length
      };
      isVirtualGroup = true;
    }
    
    // clear virtual filter
    if (this.virtualGroupActivated && !isVirtualGroup){
      this.meteringStore.dispatch(new fromMeteringStore.DeactivateVirtualGroup(this.meteringType));
      this.meteringStore.dispatch(new fromMeteringStore.SetSelection({meteringPointIds: [], meteringType: this.meteringType }));
      this.accountStore.dispatch(new fromAccountStore.FilterSettingReset(this.meteringType));
    }

    this.accountStore.select(fromAccountStore.getUserInfo).subscribe(userInfo => {
      if (userInfo) {
        if (isVirtualGroup){
          this.meteringStore.dispatch(new fromMeteringStore.ActivateVirtualGroup(this.meteringType));
          this.accountStore.dispatch(
            new fromAccountStore.FilterSettingUpdate({
              meteringType: this.meteringType,
              filter: this.currentFilter,
              userId: userInfo.id,
              filterArea: this.filterArea,
              meteringPoints: this.meteringPoints,
              meteringPointGroups: this.meteringPointGroups
            })
          );
          this.meteringStore.dispatch(new fromMeteringStore.SetSelection({meteringPointIds: this.currentFilter.containsMeteringPointIds, meteringType: this.meteringType }));
        } else {
          
          this.accountStore.dispatch(
            new fromAccountStore.FilterSettingUpdate({
              meteringType: this.meteringType,
              filter: this.currentFilter,
              userId: userInfo.id,
              filterArea: this.filterArea,
              meteringPoints: this.meteringPoints,
              meteringPointGroups: this.meteringPointGroups
            })
          );
        }
        this.dialogRef.close('save');
      }
    });
  }

  onAllMeteringPointsSelected() {
    let clearFilteringGroup = new MeteringPointGroup(); // create a fake group for unselecting userfilter
    clearFilteringGroup.id = -1;
    this.selectedVirtual = false;
    this.selectedMeteringPointGroup = clearFilteringGroup;
    this.selectedMeteringPoints = [];
    this.meteringPoints = this.meteringPoints.map(mp => ({...mp, selected: false}));
    this.meteringPointsView = [...this.meteringPoints];
    if (this.searchFilter) {
      this.updateView(this.searchFilter);  // apply searchFilter
    }
  }

  onVirtualFilterSelected = () => {
    let virtualGroup = new MeteringPointGroup(); // create a fake group for selecting virtual group
    virtualGroup.id = -2;
    this.selectedMeteringPointGroup = virtualGroup;
    this.selectedVirtual = true;
    this.selectedMeteringPoints = [];
  };

  onFilterTypeChange(args: MatRadioChange){
    this.isSearchAddresses = args.value;
  }

  onMeteringPointSelected(args: MatCheckboxChange, meteringPoint: MeteringPoint){
    this.manageMeteringPointSelection(meteringPoint, args.checked);
  }

  onLogicalMeteringPointSelected(args: MatRadioChange){
    this.selectedMeteringPoints = []; // only one logical meteringpoint can be selected
    let selectedLogical : MeteringPoint = args.value;

    this.manageMeteringPointSelection(selectedLogical, true);
  }

  private manageMeteringPointSelection(meteringPoint: MeteringPoint, isSelected: boolean){
    this.selectedMeteringPointGroup = null;
    this.selectedVirtual = false;
    this.meteringPointGroupsView = this.meteringPointGroupsView.map(mpg => ({...mpg, selected: false}));

    meteringPoint.selected = isSelected;

    if (isSelected) {
      this.selectedMeteringPoints = [...this.selectedMeteringPoints, meteringPoint ];
    } else{
      let selectedIndex = this.selectedMeteringPoints.findIndex(mp => mp.id === meteringPoint.id);
      if (selectedIndex >= 0){
        this.selectedMeteringPoints.splice(selectedIndex, 1);
      }
      if (this.selectedMeteringPoints.length === 0){
        this.onAllMeteringPointsSelected();
      }
    }

    //console.log(`metering-filter-config\tmanageMeteringPointSelection\tselectedMeteringPoints`, this.selectedMeteringPoints);
    let index = this.meteringPoints.findIndex(mp => mp.id === meteringPoint.id);
    if (index >= 0) {
      this.meteringPoints[index] = {...this.meteringPoints[index], selected: isSelected};
    }
    

    this.meteringPointsView = [...this.meteringPoints];
    if (this.searchFilter) {
      this.updateView(this.searchFilter);  // apply searchFilter
    }
    
  }

  onSelectMeteringGroup(event: MatRadioChange) {
    this.selectedMeteringPointGroup = event.value;
    this.selectedMeteringPoints = [];
    this.meteringPoints = this.meteringPoints.map(mp => { return {...mp, selected: false}});
   
    this.meteringPointsView = [...this.meteringPoints];
    if (this.searchFilter) {
      this.updateView(this.searchFilter);  // apply searchFilter
    }
    //this.changeDetectorRef.detectChanges();
    //console.log(`metering-filter-config\tselectMeteringGroup\tselectedMeteringPoints`, this.selectedMeteringPoints);
    //console.log(`metering-filter-config\tselectMeteringGroup\tselectedMeteringPointGroup`, this.selectedMeteringPointGroup);
    //this.updateView(this.searchFilter);
  }

  foundInAdresses = (addresses: MeteringPointAddress[], filterString: string): boolean => {
    const matched = addresses.filter(address =>
      containsCaseInsensitive(address.street + ' ' + address.streetNo, filterString)
    );
    return matched.length > 0;
  };
}
